import { ColumnWidthOutlined } from '@ant-design/icons';
import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { ConfigFormItem } from '@framework/engine/ui/form/ConfigFormItem.tsx';
import {
  type RelationEditor,
  useRelationEditor,
} from '@framework/form/form/relations/useRelationEditor.ts';
import { DividerTitleElement } from '@framework/ui/components/text/DividerTitleElement.tsx';
import { Button, Flex, message, Tooltip } from 'antd';
import { type Feature, Map as OLMap, View } from 'ol';
import { useTranslation } from 'react-i18next';
import 'ol/ol.css';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { OpenLayersMap } from '../../../maps/OpenLayerMap.tsx';
import { MapDisabledHandler } from './components/MapDisabledHandler.tsx';
import { MapGeometriesTable } from './components/MapGeometriesTable.tsx';
import { MapIdentifyLiveRegion } from './components/MapIdentifyLiveRegion.tsx';
import { MapIdentifyResultsPanel } from './components/MapIdentifyResultsPanel.tsx';
import { MapIntersectionLayerSection } from './components/MapIntersectionLayerSection.tsx';
import { MapToolHint } from './components/MapToolHint.tsx';
import {
  getIntersectionFieldSuffix,
  MapCadastralSuffix,
  type MapGeometryItem,
  type MapIntersectionBaseItem,
} from './createMapSchema.ts';
import { fitViewToFeatures } from './drawing/convertGeometryItemsToFeatures.ts';
import { MapUploadGeometriesControl } from './drawing/file/MapUploadGeometriesControl.tsx';
import { MapIdentifySelectionControls } from './drawing/identify/MapIdentifySelectionControls.tsx';
import {
  type IdentifySelectionMode,
  useMapIdentifyInteractions,
} from './drawing/identify/useMapIdentifyInteractions.ts';
import { useMapIdentifyManager } from './drawing/identify/useMapIdentifyManager.ts';
import { useMapIntersectionManager } from './drawing/intersection/useMapIntersectionManager.ts';
import { MapDrawingControls } from './drawing/MapDrawingControls.tsx';
import { MapRestoreDefaultControl } from './drawing/MapRestoreDefaultControl.tsx';
import { MapSearchAddressControl } from './drawing/MapSearchAddressControl.tsx';
import { MapSearchCadastralControl } from './drawing/MapSearchCadastralControl.tsx';
import { useMapDrawingInteractions } from './drawing/useMapDrawingInteractions.tsx';
import { useMapGeometryCounter } from './drawing/useMapGeometryCounter.ts';
import { useMapRestoreDefaultHandler } from './drawing/useMapRestoreDefaultHandler.ts';
import { useMapSubjectHandler } from './drawing/useMapSubjectHandler.ts';
import { MapExtendedViewModal } from './extended-view/MapExtendedViewModal.tsx';
import { MapExtendedViewPlaceholder } from './extended-view/MapExtendedViewPlaceholder.tsx';
import { MapExtendedViewToggleControl } from './extended-view/MapExtendedViewToggleControl.tsx';
import { MapHostOutlet } from './extended-view/MapHostOutlet.tsx';
import { useMapExtendedView } from './extended-view/useMapExtendedView.ts';
import { type IMapWidget, MapWidgetSelectionShape } from './IMapWidget.ts';
import { buildGroupedLayers } from './layers/controls/buildGroupedLayers.ts';
import { MapControls } from './layers/controls/MapControls.tsx';
import { getIntersectionLayersInfo } from './layers/getIntersectionLayersInfo.ts';
import { creteMapLayerFromConfig } from './layers/rendering/createMapLayerFromConfig.ts';
import { MapWidgetLogic } from './MapWidgetLogic.ts';

const MapControlsTopRow = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  margin-left: 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  /* Il contenitore non deve intercettare i click nelle zone vuote: così pan e
     zoom della mappa restano possibili. Solo i figli (toolbar e pannello
     risultati) ricevono gli eventi. */
  pointer-events: none;
  /* Le righe restano a contenuto e a destra, ma non superano la larghezza
     disponibile: in inline (mappa stretta) i tool vanno a capo come prima. */
  & > * {
    max-width: 100%;
    pointer-events: auto;
  }
  .ant-radio-button-wrapper-disabled,
  .ant-btn:disabled.ant-btn-default {
    background-color: #f3f3f3;
  }
`;

const MapContainer = styled.div`
  position: relative;
  height: 600px;
`;

// ant-btn ant-btn-default ant-btn-color-default ant-btn-variant-outlined ant-btn-icon-only
export interface IMapWidgetComponentProps
  extends IWidgetComponentProps<IMapWidget> {}

export type MapSubjectEvent =
  | { type: 'add'; feature: Feature; addToSource?: boolean }
  | { type: 'upload'; features: Feature[] }
  | { type: 'modify'; feature: Feature }
  | { type: 'remove'; feature: Feature }
  | { type: 'removeId'; id: string }
  | { type: 'reset' }
  // Ripristina le geometrie dal valore originale FO (tag semantico): pulisce e
  // ricarica le feature senza ricalcolare le intersezioni (che vengono
  // ripristinate direttamente dal chiamante).
  | { type: 'restore'; geometries: MapGeometryItem[] };

export function MapWidgetComponent(props: IMapWidgetComponentProps) {
  const { widget } = props;
  const options = getWidgetProps(props);
  const { t } = useTranslation();
  const editor = useRelationEditor<MapGeometryItem>(options.name);
  const { getNextIndex, getNextIndexBatch } = useMapGeometryCounter(
    editor,
    options.readonly,
  );
  const mapRef = React.useRef<OLMap | null>(null);
  /**
   * Diventa `true` al termine di `initMap`. Serve come dipendenza reattiva
   * per l'effetto di riconciliazione del default asincrono: `mapRef` è una
   * ref e non fa scattare gli effetti quando la mappa viene inizializzata.
   */
  const [isMapReady, setIsMapReady] = useState(false);

  const {
    isExtendedView,
    toggle: toggleExtendedView,
    close: closeExtendedView,
    hostNode: mapHostNode,
    requestMapResize,
  } = useMapExtendedView({ mapRef });

  /** Tipologie di geometria abilitate nella configurazione. */
  const shapes = widget.selection?.shapes ?? [MapWidgetSelectionShape.Polygon];

  /**
   * Tutti i layer con intersezione abilitata: montiamo ogni sezione (anche le
   * custom in profilo basic) così il relativo editor si registra; è poi la
   * singola sezione a decidere se mostrare la tabella.
   */
  const intersectionLayersInfo = useMemo(
    () => getIntersectionLayersInfo(widget),
    [widget],
  );

  const [identifySelectionMode, setIdentifySelectionMode] =
    useState<IdentifySelectionMode>('point');

  /**
   * Registry degli editor per tutti i layer di intersezione
   * (catastali e custom), indicizzato per codice intersezione.
   */
  const intersectionEditorsRef = useRef(
    new Map<string, RelationEditor<MapIntersectionBaseItem>>(),
  );

  /**
   * Callback usata dai sub-component MapIntersectionLayerSection
   * per registrare il proprio editor nel registry.
   */
  const registerEditor = useCallback(
    (
      intersectionCode: string,
      editorRef: RelationEditor<MapIntersectionBaseItem>,
    ) => {
      intersectionEditorsRef.current.set(intersectionCode, editorRef);
    },
    [],
  );

  const {
    featuresSource,
    mode,
    handleModeChange,
    initDrawing,
    interactions,
    activeShape,
    updateActiveShape,
  } = useMapDrawingInteractions({
    widget,
    geometriesEditor: editor,
  });

  const { triggerIntersections, triggerIntersectionsIfStale, resetCompetence } =
    useMapIntersectionManager({
      featuresSource,
      widget,
      fieldName: options.name,
      intersectionEditorsRef,
      readonly: options.readonly,
    });

  /**
   * Resetta tutti gli editor di intersezione custom e
   * competenze (usata nel reset globale delle geometrie).
   */
  const resetIntersectionEditors = useCallback(() => {
    for (const ed of intersectionEditorsRef.current.values()) {
      ed.reset();
    }
    resetCompetence();
  }, [resetCompetence]);

  const {
    executeIdentify,
    results: identifyResults,
    isLoading: identifyLoading,
    clearResults: clearIdentifyResults,
    initIdentifyManager,
    hasIdentifyLayers,
  } = useMapIdentifyManager({ widget });

  const { clearSelection } = useMapIdentifyInteractions({
    mapRef,
    mode,
    identifySelectionMode,
    widget,
    onSelectionComplete: executeIdentify,
  });

  /** Chiudendo il pannello si rimuove anche la selezione sulla mappa. */
  const handleIdentifyClose = useCallback(() => {
    clearIdentifyResults();
    clearSelection();
  }, [clearIdentifyResults, clearSelection]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear solo al cambio mode
  useEffect(() => {
    if (mode !== 'identify') clearIdentifyResults();
  }, [mode]);

  const mapSubject = useMapSubjectHandler({
    editor,
    featuresSource,
    widget,
    mapRef,
    getNextIndex,
    getNextIndexBatch,
    triggerIntersections,
    resetIntersectionEditors,
    t,
  });

  const handleRestoreDefault = useMapRestoreDefaultHandler({
    mapSubject,
    triggerIntersections,
  });

  /**
   * Guardia one-shot per la riconciliazione all'avvio: evita di ripetere
   * restore/ricalcolo (e le relative richieste WFS) quando `editor.items`
   * cambia identità nei render successivi.
   */
  const startupReconciledRef = useRef(false);

  /**
   * Riconcilia le geometrie e le tabelle derivate all'avvio del widget.
   *
   * Poiché `initMap` gira una sola volta al mount del div — quando le
   * geometrie possono non essere ancora arrivate (default asincrono dal tag
   * semantico) — non possiamo ricalcolare lì. Reagiamo invece alla presenza
   * delle geometrie (`editor.items`) una volta che la mappa è pronta:
   * - se il source è vuoto (geometrie arrivate dopo `initDrawing`) le
   *   ricarichiamo via l'evento `restore`, che pulisce, ricarica le feature e
   *   centra la vista;
   * - ricalcoliamo intersezioni e competenze se la configurazione è cambiata
   *   dall'ultimo calcolo salvato. Copre sia il default dal tag (che popola
   *   solo le geometrie, senza hash) sia la bozza riaperta dopo una
   *   modifica al widget.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: agiamo all'arrivo di isMapReady/editor.items; le altre dipendenze sono ref o valori stabili derivati dalla config widget
  useEffect(() => {
    if (options.readonly) return;
    if (!isMapReady || startupReconciledRef.current) return;
    if (editor.items.length === 0) return;

    if (featuresSource.current.getFeatures().length === 0) {
      mapSubject.current.next({ type: 'restore', geometries: editor.items });
    }
    triggerIntersectionsIfStale();
    startupReconciledRef.current = true;
  }, [isMapReady, editor.items]);

  // Solamente in anteprima, quando la mappa è stata centrata e zoommata
  // nel pannello di configurazione, allora la mappa deve essere centrata
  useEffect(() => {
    if (!options.preview) return;
    if (widget.position?.center && widget.position?.zoom) {
      mapRef.current?.getView().setCenter(widget.position.center);
      mapRef.current?.getView().setZoom(widget.position.zoom);
    }
  }, [widget.position, options.preview]);

  /**
   * Il container mappa può passare per uno stato 0×0 sia quando la tab
   * keep-alive viene nascosta con `display:none`, sia durante lo spostamento
   * del nodo tra inline e fullscreen: al ritorno la view può risultare
   * degenerata (tile a coordinate inesistenti → 404).
   *
   * Vogliamo ricentrare sulle geometrie SOLO al ritorno da un cambio tab, non
   * in fullscreen (dove va preservata la posizione dell'utente). I due casi si
   * distinguono dal `parentElement` del nodo mappa: il fullscreen lo sposta in
   * un altro container (parent cambia), il cambio tab no. Quindi ricentriamo
   * solo se il nodo è tornato visibile senza essere stato spostato; in ogni
   * caso ricalcoliamo la size.
   */
  useEffect(() => {
    let wasHidden = false;
    let lastParent = mapHostNode.parentElement;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width === 0 || height === 0) {
          wasHidden = true;
          continue;
        }
        const parentChanged = mapHostNode.parentElement !== lastParent;
        lastParent = mapHostNode.parentElement;
        const cameBackFromTab = wasHidden && !parentChanged;
        wasHidden = false;
        requestAnimationFrame(() => {
          const map = mapRef.current;
          if (!map) return;
          map.updateSize();
          if (cameBackFromTab) {
            fitViewToFeatures(map, featuresSource.current);
          }
        });
      }
    });
    observer.observe(mapHostNode);
    return () => observer.disconnect();
  }, [mapHostNode, featuresSource]);

  const initMap = useCallback(
    (element: HTMLDivElement) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[MapWidgetComponent] initMap`, element);
      }
      const { zOrder } = buildGroupedLayers(
        widget.layers ?? [],
        widget.groups ?? [],
      );
      const map = new OLMap({
        target: element,
        layers: (widget.layers ?? []).map((layer, index) => {
          const l = creteMapLayerFromConfig(layer);
          // `code` è l'identificatore stabile del layer, usato dai controlli
          // UI (es. pannello "Livelli") per pilotarne la visibilità.
          l.set('code', layer.code);
          l.setZIndex(zOrder[index]);
          return l;
        }),
        view: new View({
          center: widget.position?.center ?? [
            11.189_417_912_530_622, 44.576_276_442_981_985,
          ],
          zoom: widget.position?.zoom ?? 4,
          // Cap di sicurezza: senza `maxZoom` il default di OpenLayers è 28. Se
          // la view degenera — es. un `fit` su container 0×0 quando la tab che
          // ospita la mappa è nascosta con `display:none` — la mappa
          // richiederebbe tile a z28 inesistenti (404).
          maxZoom: 27,
        }),
      });

      // Aggiungo il layer delle geometrie disegnate
      initDrawing(map);
      // Inizializza il manager identify solo se esistono layer interrogabili
      // (con funzionalità avanzate disattivate l'elenco è vuoto).
      if (hasIdentifyLayers) {
        initIdentifyManager();
      }

      // Una Draw per ogni shape configurato: al termine del disegno la
      // geometria viene aggiunta all'editor.
      Object.values(interactions.current.draws).forEach(draw => {
        draw?.on('drawend', event => {
          mapSubject.current.next({ type: 'add', feature: event.feature });
        });
      });

      interactions.current.modify!.on('modifyend', async event => {
        mapSubject.current.next({
          type: 'modify',
          feature: event.features.item(0),
        });
      });
      interactions.current.translate?.on('translateend', event => {
        mapSubject.current.next({
          type: 'modify',
          feature: event.features.item(0),
        });
      });

      interactions.current.remove!.on('select', event => {
        if (event.selected.length === 0) return;
        mapSubject.current.next({ type: 'remove', feature: event.selected[0] });
      });

      mapRef.current = map;
      setIsMapReady(true);
      return map;
    },
    [
      widget.layers,
      widget.groups,
      widget.position?.center,
      widget.position?.zoom,
      initDrawing,
      initIdentifyManager,
      // Stabile a runtime (deriva da config widget + contesto submission):
      // non fa cambiare identità a initMap, quindi la mappa non viene ricreata.
      hasIdentifyLayers,
      interactions,
    ],
  );

  const isMobile = useBreakpoint(down('lg'));

  return (
    <ConfigFormItem
      className="map-widget-component-form-item"
      name={options.name}
      label={<RichTextAdvancedRender text={options.label} />}
      help={<RichTextAdvancedRender text={options.helpText} />}
      layout={{
        hideVisualLabel: options.hideLabel,
      }}
      widget={props.widget}
    >
      <MapDisabledHandler mapSubject={mapSubject.current} />
      <MapIdentifyLiveRegion
        results={identifyResults}
        isLoading={identifyLoading}
      />
      {/*
        La mappa vive in un nodo DOM stabile (mapHostNode) reso una sola volta
        via portal: viene spostata tra slot inline e Modal senza rimontarla né
        ricrearla (vedi MapHostOutlet / useMapExtendedView).
      */}
      {createPortal(
        <OpenLayersMap
          init={initMap}
          // In estesa la mappa è a filo con la Modal: niente bordo né raggi.
          style={
            isExtendedView
              ? { height: '100%', border: 'none', borderRadius: 0 }
              : { height: '100%' }
          }
          controls={
            <>
              <MapControlsTopRow>
                <Flex gap="0.5rem" wrap justify="flex-end">
                  <MapSearchAddressControl map={mapRef} isMobile={isMobile} />
                  {!options.readonly && (
                    <MapSearchCadastralControl
                      map={mapRef}
                      mapSubject={mapSubject}
                      isMobile={isMobile}
                      municipalityGroupList={widget.municipalityGroupList}
                    />
                  )}
                  {!options.readonly && (
                    <MapDrawingControls
                      onModeChange={handleModeChange}
                      mode={mode}
                      showIdentify={hasIdentifyLayers}
                      isMobile={isMobile}
                      shapes={shapes}
                      activeShape={activeShape}
                      onActiveShapeChange={updateActiveShape}
                    />
                  )}
                  {!options.readonly && (
                    <MapUploadGeometriesControl mapSubject={mapSubject} />
                  )}
                  {!options.readonly &&
                    !options.preview &&
                    widget.defaultValueReference != null && (
                      <MapRestoreDefaultControl
                        fieldName={options.name}
                        reference={widget.defaultValueReference}
                        onRestore={handleRestoreDefault}
                      />
                    )}
                  <div>
                    <Tooltip
                      title={t(
                        'map-widget.title',
                        'Centra mappa sulle geometrie',
                      )}
                    >
                      <Button
                        type="default"
                        style={{ borderWidth: '1px' }}
                        icon={<ColumnWidthOutlined aria-label="Centra" />}
                        onClick={() => {
                          if (
                            featuresSource.current.getFeatures().length === 0
                          ) {
                            message.info(
                              t(
                                'map-widget.no-geometries',
                                'Nessuna geometria presente',
                              ),
                            );
                            return;
                          }

                          mapRef.current
                            ?.getView()
                            .fit(featuresSource.current.getExtent(), {
                              padding: [64, 64, 64, 64],
                              maxZoom: 19,
                            });
                        }}
                      />
                    </Tooltip>
                  </div>
                  {MapWidgetLogic.isAdvancedMapWidgetEnabled() && (
                    <MapExtendedViewToggleControl
                      isExtendedView={isExtendedView}
                      onToggle={toggleExtendedView}
                    />
                  )}
                </Flex>
                {mode === 'identify' && (
                  <Flex gap="0.5rem" wrap justify="flex-end">
                    <MapIdentifySelectionControls
                      selectionMode={identifySelectionMode}
                      onSelectionModeChange={setIdentifySelectionMode}
                      isMobile={isMobile}
                    />
                  </Flex>
                )}
                {/*
                  In vista estesa il pannello risultati è un overlay flottante
                  che vive nella stessa colonna della toolbar: si posiziona
                  automaticamente sotto i tool, senza mai coprirli.
                */}
                {isExtendedView && (
                  <MapIdentifyResultsPanel
                    floating
                    results={identifyResults}
                    isLoading={identifyLoading}
                    onClose={handleIdentifyClose}
                    isMobile={isMobile}
                  />
                )}
              </MapControlsTopRow>
              {/*
                Mini-tutorial contestuale: banda in basso, centrata sulla
                mappa, con il gesto d'uso dello strumento attivo. Vive nella
                stessa portal dei controlli, quindi compare sia inline sia in
                vista estesa.
              */}
              <MapToolHint
                mode={mode}
                activeShape={activeShape}
                identifySelectionMode={identifySelectionMode}
              />
              <MapControls map={mapRef} widget={widget} />
            </>
          }
        />,
        mapHostNode,
      )}
      <MapContainer>
        {isExtendedView ? (
          <MapExtendedViewPlaceholder onReduce={closeExtendedView} />
        ) : (
          <MapHostOutlet
            hostNode={mapHostNode}
            style={{ width: '100%', height: '100%' }}
            onAttach={requestMapResize}
          />
        )}
      </MapContainer>
      <MapExtendedViewModal
        open={isExtendedView}
        isMobile={isMobile}
        onClose={closeExtendedView}
        onOpenChange={requestMapResize}
      >
        <MapHostOutlet
          hostNode={mapHostNode}
          style={{ width: '100%', height: '100%' }}
          onAttach={requestMapResize}
        />
      </MapExtendedViewModal>
      {!isExtendedView && (
        <MapIdentifyResultsPanel
          results={identifyResults}
          isLoading={identifyLoading}
          onClose={handleIdentifyClose}
        />
      )}
      {!widget.selection?.hideGeometriesTable && (
        <div>
          <DividerTitleElement
            title={t('map-widget.drawn-geometries', 'Geometrie disegnate')}
          />
          <MapGeometriesTable
            editor={editor}
            readonly={options.readonly}
            mapRef={mapRef}
            featuresSource={featuresSource}
            geometryLabelPrefix={widget.labeling?.prefix}
            onRemove={id =>
              mapSubject.current.next({
                type: 'removeId',
                id,
              })
            }
          />
        </div>
      )}
      {intersectionLayersInfo.map(({ layer, code }) => (
        <MapIntersectionLayerSection
          key={`intersection-${code}`}
          layer={layer}
          fieldName={
            layer.intersection.cadastralPreset
              ? `${options.name}${MapCadastralSuffix}`
              : `${options.name}${getIntersectionFieldSuffix(code)}`
          }
          registerEditor={registerEditor}
          mapRef={mapRef}
          featuresSource={featuresSource}
          readonly={options.readonly}
          geometryLabelPrefix={widget.labeling?.prefix}
        />
      ))}
    </ConfigFormItem>
  );
}
